import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";
import { BLOG_POSTS } from "../blogData";

export default function Blog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [featured, ...rest] = BLOG_POSTS;

  return (
    <>
      <SEO
        title="Blog — Home Service Marketing That Books Jobs"
        description="Straight-talk guides for Houston home service pros: stop missing calls, get more Google reviews, and turn your website into a lead machine."
        path="/blog"
      />
      <Navbar />
      <main className="bg-ls">
        <section className="relative overflow-hidden pt-28 md:pt-36 pb-12 grain">
          <div
            className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, #f26a21, transparent 70%)" }}
          />
          <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
            <p className="ls-eyebrow mb-4">The playbook</p>
            <h1 className="font-display text-white text-[13vw] sm:text-6xl md:text-7xl max-w-4xl">
              Booked jobs, <span className="text-orange">explained.</span>
            </h1>
            <p className="mt-5 text-lg text-[#c7bdae] max-w-2xl">
              No fluff. Just how home service pros in Houston book more work — and stop leaving money on the table.
            </p>
          </div>
        </section>

        {/* Featured */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-8">
          <Link
            to={`/blog/${featured.slug}`}
            className="group grid lg:grid-cols-2 gap-8 rounded-2xl overflow-hidden card-dark p-0"
          >
            <div className="bg-black">
              <img src={featured.image} alt={featured.title} className="w-full h-full aspect-video lg:aspect-auto object-cover min-h-[260px]" />
            </div>
            <div className="p-8 flex flex-col justify-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-orange mb-3">
                {featured.category}
              </span>
              <h2 className="font-cond font-extrabold text-3xl md:text-4xl text-white uppercase leading-tight group-hover:text-orange transition-colors">
                {featured.title}
              </h2>
              <p className="mt-4 text-[#b7ab9b] leading-relaxed">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-sm text-[#9a8f80]">
                <span>{featured.date}</span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {featured.readTime}
                </span>
              </div>
              <span className="mt-5 inline-flex items-center gap-2 text-orange font-cond font-bold uppercase tracking-wide">
                Read it <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        </section>

        {/* Grid */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
          <div className="grid md:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="card-dark overflow-hidden group flex flex-col">
                <div className="bg-black overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-orange mb-2">
                    {p.category}
                  </span>
                  <h3 className="font-cond font-bold text-xl text-white leading-tight group-hover:text-orange transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[#b7ab9b] text-sm leading-relaxed flex-1">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-[#9a8f80]">
                    <span>{p.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {p.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
